// TODO(APP-8243): Fix type conversions in the fragment variables project

import type { JsonObject } from 'type-fest';

import { getDisplayNameFromVariable } from '$lib/config-reader/variables';
import type {
  RawBillingConfig,
  RawJobConfig,
  RawProcessConfig,
  RawRemoteAuthConfig,
  RawRemoteAuthCredentialsConfig,
  RawRemoteConfig,
  RawTriggerConfig,
  RawTriggerEval,
  RawTriggerEvent,
} from '$lib/config-schemas';
import {
  hasVariables,
  isVariableObject,
  type VariableObject,
} from '$lib/config-schemas/has-variables';
import type {
  RawLocalModuleConfig,
  RawRegistryModuleConfig,
} from '$lib/config-schemas/raw-module';
import { isBuiltinModel } from '$lib/rdk';
import {
  type APITriplet,
  joinResourceTriplet,
  type ModelTriplet,
  RDK_MODEL_NAME_BUILTIN,
  RDK_NAMESPACE,
  type ResourceTripletString,
  splitAPITriplet,
  splitModelTriplet,
} from '$lib/resource-type';
import { sortObjectKeys } from '$lib/sort';
import { stripUndefined } from '$lib/strip-undefined';

import {
  type FragmentID,
  type FragmentPrefix,
  RegexOperator,
  SingletonResourceTypes,
} from './config-item';
import * as ConfigItem from './config-item';
import type * as Schemas from './config-schemas';
import type { ReadonlyMachineConfigPart } from './machine-config';
import {
  heartbeatWindowNumberToString,
  heartbeatWindowStringToNumber,
} from './network';
import { isLivenessTriggerType, TriggerTypes } from './trigger';

/** A function to create a resource ID from its name and type. */
export type CreateResourceID = (
  resourceType: ConfigItem.ResourceType,
  resourceName: string
) => ConfigItem.ResourceID;

const getOrCreateID = (
  idOrCreate: ConfigItem.ResourceID | CreateResourceID,
  resourceType: ConfigItem.ResourceType,
  resourceName: string
): ConfigItem.ResourceID => {
  let name = resourceName;
  if (hasVariables(resourceName)) {
    name = getDisplayNameFromVariable(
      resourceName as unknown as VariableObject
    );
  }
  return typeof idOrCreate === 'function'
    ? idOrCreate(resourceType, name)
    : idOrCreate;
};

/** Normalize agent configs. */
export const toRawAgent = (
  agent: ConfigItem.ReadonlyAgent | undefined
): Schemas.RawAgentConfig | undefined => {
  if (agent === undefined) {
    return undefined;
  }

  const {
    versionControl,
    advancedSettings,
    networkConfiguration,
    additionalNetworks,
    systemConfiguration,
    otherProps,
  } = agent;

  return stripUndefined({
    version_control: versionControl,
    advanced_settings: advancedSettings,
    network_configuration: networkConfiguration,
    additional_networks: additionalNetworks,
    system_configuration: systemConfiguration,
    ...otherProps,
  });
};

export const fromRawAgent = (
  agent: Schemas.RawAgentConfig | undefined
): ConfigItem.ReadonlyAgent | undefined => {
  if (agent === undefined) {
    return undefined;
  }

  const sortedAgent = sortObjectKeys(agent);
  const {
    version_control: versionControl,
    advanced_settings: advancedSettings,
    network_configuration: networkConfiguration,
    additional_networks: additionalNetworks,
    system_configuration: systemConfiguration,
    ...otherProps
  } = sortedAgent;

  return stripUndefined({
    type: ConfigItem.SingletonResourceTypes.AGENT,
    versionControl,
    advancedSettings,
    networkConfiguration,
    additionalNetworks,
    systemConfiguration,
    otherProps,
  });
};

/** Normalize billing configs. */
export const toRawBilling = (
  billing: ConfigItem.ReadonlyBilling | undefined
): RawBillingConfig | undefined => {
  if (billing === undefined) {
    return undefined;
  }

  const { otherProps } = billing;

  return stripUndefined({
    ...otherProps,
  });
};

export const fromRawBilling = (
  billing: RawBillingConfig | undefined
): ConfigItem.ReadonlyBilling | undefined => {
  if (billing === undefined) {
    return undefined;
  }

  const sorted = sortObjectKeys(billing);
  const { ...otherProps } = sorted;

  return stripUndefined({
    type: SingletonResourceTypes.BILLING,
    otherProps,
  });
};

/** Normalize network configs. */
export const fromRawNetwork = (
  rawNetwork: Schemas.RawNetworkConfig | undefined
): ConfigItem.ReadonlyNetwork | undefined => {
  if (rawNetwork === undefined) {
    return undefined;
  }
  const { bind_address: bindAddress, sessions, ...otherProps } = rawNetwork;
  const { heartbeat_window: heartbeatWindow } = sessions ?? {};
  return {
    type: ConfigItem.SingletonResourceTypes.NETWORK,
    bindAddress,
    heartbeatWindowS: heartbeatWindow
      ? heartbeatWindowStringToNumber(heartbeatWindow)
      : undefined,
    otherProps,
  };
};

export const toRawNetwork = (
  network: ConfigItem.ReadonlyNetwork | undefined
): Schemas.RawNetworkConfig | undefined => {
  if (!network) {
    return undefined;
  }

  const networkConfig: Schemas.RawNetworkConfig = {};
  if (network.bindAddress) {
    networkConfig.bind_address = network.bindAddress;
  }
  if (network.heartbeatWindowS) {
    networkConfig.sessions = {
      heartbeat_window: heartbeatWindowNumberToString(network.heartbeatWindowS),
    };
  }

  return stripUndefined({
    ...networkConfig,
    ...network.otherProps,
  });
};

/** Normalize maintenance configs. */
export const fromRawMaintenance = (
  rawMaintenance: Schemas.RawMaintenanceConfig | undefined
): ConfigItem.ReadonlyMaintenance | undefined => {
  if (rawMaintenance === undefined) {
    return undefined;
  }
  const {
    maintenance_allowed_key: maintenanceAllowedKey,
    sensor_name: sensorName,
    ...otherProps
  } = rawMaintenance;
  return {
    type: ConfigItem.SingletonResourceTypes.MAINTENANCE,
    maintenanceAllowedKey,
    sensorName,
    otherProps,
  };
};

export const toRawMaintenance = (
  maintenance: ConfigItem.ReadonlyMaintenance | undefined
): Schemas.RawMaintenanceConfig | undefined => {
  if (!maintenance) {
    return undefined;
  }

  const maintenanceRaw: Schemas.RawMaintenanceConfig = {
    maintenance_allowed_key: maintenance.maintenanceAllowedKey,
    sensor_name: maintenance.sensorName,
    ...maintenance.otherProps,
  };

  return stripUndefined(maintenanceRaw);
};

const fromRawCaptureMethod = (
  rawCaptureMethod: Schemas.RawDataManagerCaptureMethodConfig
): ConfigItem.ReadonlyCaptureMethod => {
  const {
    method,
    capture_frequency_hz: captureFrequencyHz,
    disabled = false,
    additional_params: rawAdditionalParams,
    ...otherProps
  } = rawCaptureMethod;

  const {
    mime_type: mimeType,
    pin_name: pinName,
    reader_name: readerName,
    camera_name: cameraName,
    docommand_input: docommandInput,
    filter_source_names: filterSourceNames,
    ...additionalParamsOtherProps
  } = rawAdditionalParams ?? {};

  return {
    method,
    captureFrequencyHz,
    disabled,
    additionalParams: {
      mimeType,
      cameraName,
      otherParams: additionalParamsOtherProps,
      pinName,
      readerName,
      docommandInput: docommandInput as Record<string, unknown> | undefined,
      filterSourceNames,
    },
    otherProps,
  };
};

const toRawCaptureMethod = (
  captureMethod: ConfigItem.ReadonlyCaptureMethod
): Schemas.RawDataManagerCaptureMethodConfig =>
  stripUndefined({
    method: captureMethod.method,
    capture_frequency_hz: captureMethod.captureFrequencyHz,
    disabled: captureMethod.disabled,
    additional_params: {
      mime_type: captureMethod.additionalParams.mimeType,
      camera_name: captureMethod.additionalParams.cameraName,
      pin_name: captureMethod.additionalParams.pinName,
      reader_name: captureMethod.additionalParams.readerName,
      docommand_input: captureMethod.additionalParams.docommandInput,
      filter_source_names: captureMethod.additionalParams.filterSourceNames
        ? [...captureMethod.additionalParams.filterSourceNames]
        : undefined,
      ...captureMethod.additionalParams.otherParams,
    },
    ...captureMethod.otherProps,
  });

const fromRawAssociatedService = (
  rawAssociatedService: Schemas.RawAssociatedServiceConfig
): ConfigItem.ReadonlyAssociatedService => {
  const { type, attributes, ...otherProps } = rawAssociatedService;
  const { capture_methods: captureMethods, ...otherAttributes } = attributes;

  const machineConfigCaptureMethods =
    captureMethods?.map(
      (rawCaptureMethod: Schemas.RawDataManagerCaptureMethodConfig) =>
        fromRawCaptureMethod(rawCaptureMethod)
    ) ?? [];

  return {
    type: type as ConfigItem.AssociatedServiceType,
    attributes: {
      captureMethods: machineConfigCaptureMethods,
      otherAttributes,
    },
    otherProps,
  };
};

const toRawAssociatedService = (
  associatedService: ConfigItem.ReadonlyAssociatedService | undefined
): Schemas.RawAssociatedServiceConfig | undefined => {
  if (!associatedService) {
    return undefined;
  }

  return stripUndefined({
    type: associatedService.type,
    attributes: {
      capture_methods: associatedService.attributes.captureMethods.map(
        (captureMethod) => toRawCaptureMethod(captureMethod)
      ),
      ...associatedService.attributes.otherAttributes,
    },
    ...associatedService.otherProps,
  });
};

export const fromRawUnknownResource = (
  rawResource: Schemas.RawResourceConfig
): ConfigItem.ReadonlyResource => {
  return { ...rawResource } as ConfigItem.ReadonlyResource;
};

export const fromRawComponent = (
  rawComponent: Schemas.RawComponentConfig,
  id: ConfigItem.ResourceID | CreateResourceID
): ConfigItem.ReadonlyComponent => {
  const {
    name,
    api,
    type,
    model,
    frame,
    namespace,
    attributes = {},
    depends_on: dependsOn = [],
    service_configs: associatedServices,
    disabled: isDisabled,
    log_configuration: logConfiguration,
    ui_folder: uiFolder,
    notes,
    ...otherProps
  } = rawComponent;
  const resourceType = ConfigItem.ResourceTypes.COMPONENT;
  const resourceID = getOrCreateID(id, resourceType, name);

  // Data manager is the only supported service config
  const dataManager =
    associatedServices?.[0] === undefined
      ? undefined
      : fromRawAssociatedService(associatedServices[0]);

  return {
    id: resourceID,
    type: resourceType,
    api: api
      ? splitAPITriplet(api)
      : {
          namespace: namespace ?? RDK_NAMESPACE,
          type: resourceType,
          subtype: type,
          isBackCompatFormat: true,
        },
    model: splitModelTriplet(model),
    name,
    attributes,
    frame,
    dataManager,
    dependsOn: dependsOn as string[],
    isDisabled,
    logConfiguration,
    uiFolder,
    otherProps,
    notes,
  };
};

export const toRawComponent = (
  component: ConfigItem.ReadonlyComponent
): Schemas.RawComponentConfig => {
  const associatedServices = [
    toRawAssociatedService(component.dataManager),
  ].filter((service) => service !== undefined);

  return stripUndefined({
    name: component.name,
    ...toRawAPI(component.api),
    model: toRawModel(component.model),
    attributes: component.attributes,
    depends_on:
      component.dependsOn.length > 0 ? [...component.dependsOn] : undefined,
    notes: component.notes,
    service_configs:
      associatedServices.length > 0 ? associatedServices : undefined,
    frame: component.frame,
    disabled: component.isDisabled,
    log_configuration: component.logConfiguration,
    ui_folder: component.uiFolder ?? undefined,
    ...component.otherProps,
  });
};

export const fromRawService = (
  rawService: Schemas.RawServiceConfig,
  id: ConfigItem.ResourceID | CreateResourceID
): ConfigItem.ReadonlyService => {
  const {
    name,
    api,
    type,
    model,
    namespace,
    attributes = {},
    service_configs: associatedServices,
    depends_on: dependsOn = [],
    disabled: isDisabled,
    log_configuration: logConfiguration,
    ui_folder: uiFolder,
    notes,
    ...otherProps
  } = rawService;
  const resourceType = ConfigItem.ResourceTypes.SERVICE;
  const resourceID = getOrCreateID(id, resourceType, name);

  // Data manager is the only supported service config
  const dataManager =
    associatedServices?.[0] === undefined
      ? undefined
      : fromRawAssociatedService(associatedServices[0]);

  return {
    id: resourceID,
    type: resourceType,
    api: api
      ? splitAPITriplet(api)
      : {
          namespace: namespace ?? RDK_NAMESPACE,
          type: resourceType,
          subtype: type,
          isBackCompatFormat: true,
        },
    model: splitModelTriplet(model),
    name,
    attributes,
    dataManager,
    dependsOn: dependsOn as string[],
    isDisabled,
    logConfiguration,
    uiFolder,
    otherProps,
    notes,
  };
};

export const toRawService = (
  service: ConfigItem.ReadonlyService
): Schemas.RawServiceConfig => {
  const associatedServices = [
    toRawAssociatedService(service.dataManager),
  ].filter((associatedService) => associatedService !== undefined);

  return stripUndefined({
    name: service.name,
    ...toRawAPI(service.api),
    model: toRawModel(service.model),
    attributes: service.attributes,
    depends_on:
      service.dependsOn.length > 0 ? [...service.dependsOn] : undefined,
    notes: service.notes,
    service_configs:
      associatedServices.length > 0 ? associatedServices : undefined,
    disabled: service.isDisabled,
    log_configuration: service.logConfiguration,
    ui_folder: service.uiFolder ?? undefined,
    ...service.otherProps,
  });
};

export const fromRawRegistryModule = (
  rawModule: RawRegistryModuleConfig,
  id: ConfigItem.ResourceID | CreateResourceID
): ConfigItem.ReadonlyRegistryModule => {
  const {
    name,
    module_id: moduleID,
    version,
    type: _type,
    reload_path: reloadPath,
    reload_enabled: reloadEnabled,
    reload_user: reloadUser,
    reload_time: reloadTime,
    reload_annotation: reloadAnnotation,
    disabled: isDisabled,
    notes,
    ui_folder: uiFolder,
    log_level: logLevel,
    ...otherProps
  } = rawModule;
  const resourceType = ConfigItem.ResourceTypes.REGISTRY_MODULE;
  const resourceID = getOrCreateID(id, resourceType, name);
  void _type;

  return {
    id: resourceID,
    type: resourceType,
    name,
    moduleID,
    version,
    reloadPath,
    reloadEnabled,
    reloadUser,
    reloadTime,
    reloadAnnotation,
    isDisabled,
    otherProps,
    notes,
    uiFolder,
    logLevel,
  };
};

export const toRawRegistryModule = (
  registryModule: ConfigItem.ReadonlyRegistryModule
): RawRegistryModuleConfig =>
  stripUndefined({
    type: 'registry' as const,
    name: registryModule.name,
    module_id: registryModule.moduleID,
    version: registryModule.version,
    reload_path: registryModule.reloadPath,
    reload_enabled: registryModule.reloadEnabled,
    reload_user: registryModule.reloadUser,
    reload_time: registryModule.reloadTime,
    reload_annotation: registryModule.reloadAnnotation,
    disabled: registryModule.isDisabled,
    notes: registryModule.notes,
    ui_folder: registryModule.uiFolder,
    log_level: registryModule.logLevel,
    ...registryModule.otherProps,
  });

export const fromRawLocalModule = (
  rawModule: RawLocalModuleConfig,
  id: ConfigItem.ResourceID | CreateResourceID
): ConfigItem.ReadonlyLocalModule => {
  const {
    name,
    executable_path: executablePath,
    type: _type,
    disabled: isDisabled,
    notes,
    ui_folder: uiFolder,
    log_level: logLevel,
    ...otherProps
  } = rawModule;
  const resourceType = ConfigItem.ResourceTypes.LOCAL_MODULE;
  const resourceID = getOrCreateID(id, resourceType, name);
  void _type;

  return {
    id: resourceID,
    type: resourceType,
    name,
    executablePath,
    isDisabled,
    notes,
    logLevel,
    uiFolder,
    otherProps,
  };
};

export const toRawLocalModule = (
  localModule: ConfigItem.ReadonlyLocalModule
): RawLocalModuleConfig =>
  stripUndefined({
    type: 'local' as const,
    name: localModule.name,
    executable_path: localModule.executablePath,
    disabled: localModule.isDisabled,
    notes: localModule.notes,
    ui_folder: localModule.uiFolder,
    log_level: localModule.logLevel,
    ...localModule.otherProps,
  });

const fromRawRemoteAuthCredentials = (
  rawCredentials: RawRemoteAuthCredentialsConfig
): ConfigItem.ReadonlyRemoteAuthCredentials => {
  const { type, payload, ...otherProps } = rawCredentials;
  return {
    type,
    payload,
    otherProps,
  };
};

const fromRawRemoteAuth = (
  rawAuth: RawRemoteAuthConfig
): ConfigItem.ReadonlyRemoteAuth => {
  const { credentials, entity, ...otherProps } = rawAuth;
  return {
    credentials: credentials
      ? fromRawRemoteAuthCredentials(credentials)
      : undefined,
    entity,
    otherProps,
  };
};

export const fromRawRemote = (
  rawRemote: RawRemoteConfig,
  id: ConfigItem.ResourceID | CreateResourceID
): ConfigItem.ReadonlyRemote => {
  const {
    name,
    address,
    prefix,
    insecure,
    auth,
    disabled,
    notes,
    ui_folder: uiFolder,
    frame,
    ...otherProps
  } = rawRemote;
  const resourceType = ConfigItem.ResourceTypes.REMOTE;
  const resourceID = getOrCreateID(id, resourceType, name);

  return {
    id: resourceID,
    type: resourceType,
    name,
    address,
    prefix,
    insecure,
    auth: auth ? fromRawRemoteAuth(auth) : undefined,
    isDisabled: disabled,
    frame,
    otherProps,
    notes,
    uiFolder,
  };
};

const toRawRemoteAuthCredentials = (
  credentials: ConfigItem.ReadonlyRemoteAuthCredentials
): RawRemoteAuthCredentialsConfig =>
  stripUndefined({
    ...credentials.otherProps,
    type: credentials.type,
    payload: credentials.payload,
  });

const toRawRemoteAuth = (
  auth: ConfigItem.ReadonlyRemoteAuth
): RawRemoteAuthConfig =>
  stripUndefined({
    ...auth.otherProps,
    credentials: auth.credentials
      ? toRawRemoteAuthCredentials(auth.credentials)
      : undefined,
    entity: auth.entity,
  });

export const toRawRemote = (
  remote: ConfigItem.ReadonlyRemote
): RawRemoteConfig =>
  stripUndefined({
    ...remote.otherProps,
    name: remote.name,
    address: remote.address,
    prefix: remote.prefix,
    insecure: remote.insecure,
    auth: remote.auth ? toRawRemoteAuth(remote.auth) : undefined,
    disabled: remote.isDisabled,
    frame: remote.frame,
    notes: remote.notes,
    ui_folder: remote.uiFolder,
  });

/*
 * Note: In this mapping, the 'id' field from the raw process config is used as the 'name'
 * in the process config, and the 'name' field is stored as 'executable'.
 */
export const fromRawProcess = (
  rawProcess: RawProcessConfig,
  id: ConfigItem.ResourceID | CreateResourceID
): ConfigItem.ReadonlyProcess => {
  const {
    id: name,
    name: executable,
    args,
    cwd,
    log,
    one_shot: oneShot,
    disabled: isDisabled,
    notes,
    ui_folder: uiFolder,
    ...otherProps
  } = rawProcess;
  const resourceType = ConfigItem.ResourceTypes.PROCESS;
  const resourceID = getOrCreateID(id, resourceType, name);

  return {
    id: resourceID,
    type: resourceType,
    name,
    executable,
    args: args as string[] | undefined,
    cwd,
    log,
    oneShot,
    isDisabled,
    otherProps,
    notes,
    uiFolder,
  };
};

/*
 * Note: This function reverses the mapping applied in `fromRawProcess`, storing the 'name'
 * of the config as 'id' in the raw config, and the 'executable' as 'name'.
 */
export const toRawProcess = (
  process: ConfigItem.ReadonlyProcess
): RawProcessConfig =>
  stripUndefined({
    ...process.otherProps,
    id: process.name,
    name: process.executable,
    args: process.args ? [...process.args] : undefined,
    cwd: process.cwd,
    log: process.log,
    one_shot: process.oneShot,
    disabled: process.isDisabled,
    notes: process.notes,
  });

/*
 * Note: This function maps the 'type' to `packageType`
 */
export const fromRawPackage = (
  rawPackage: Schemas.RawPackageConfig,
  id: ConfigItem.ResourceID | CreateResourceID
): ConfigItem.ReadonlyPackage => {
  const {
    name,
    package: packageID,
    type: packageType,
    version,
    disabled: isDisabled,
    notes,
    ui_folder: uiFolder,
    ...otherProps
  } = rawPackage;
  const resourceType = ConfigItem.ResourceTypes.PACKAGE;
  const resourceID = getOrCreateID(id, resourceType, name);

  return {
    id: resourceID,
    name,
    type: resourceType,
    packageID,
    version,
    packageType,
    isDisabled,
    otherProps,
    notes,
    uiFolder,
  };
};

/*
 * Note: This function reverses the mapping applied in `fromRawPackage`, storing the 'type' as `packageType`
 */
export const toRawPackage = (
  pkg: ConfigItem.ReadonlyPackage
): Schemas.RawPackageConfig =>
  stripUndefined({
    ...pkg.otherProps,
    name: pkg.name,
    package: pkg.packageID,
    type: pkg.packageType,
    version: pkg.version,
    disabled: pkg.isDisabled,
    notes: pkg.notes,
    ui_folder: pkg.uiFolder,
  });

export const fromRawTrigger = (
  rawTrigger: RawTriggerConfig,
  id: ConfigItem.ResourceID | CreateResourceID,
  components: Record<string, ConfigItem.ReadonlyComponent>
): ConfigItem.ReadonlyTrigger => {
  const {
    name,
    event,
    notifications,
    disabled: isDisabled,
    notes,
    ui_folder: uiFolder,
    ...otherProps
  } = rawTrigger;
  const resourceType = ConfigItem.ResourceTypes.TRIGGER;
  const resourceID = getOrCreateID(id, resourceType, name);
  const fallbackEvent = { type: TriggerTypes.PART_ONLINE };

  return {
    type: resourceType,
    id: resourceID,
    name,
    event: event ? fromRawTriggerEvent(event, components) : fallbackEvent,
    notifications: (notifications ?? []).map((notification) => ({
      type: notification.type ?? 'webhook',
      secondsBetweenNotifications:
        notification.seconds_between_notifications ?? 1,
      value: notification.value ?? '',
      ...(notification.type === 'push' && {
        application: notification.application ?? '',
      }),
      ...((notification.type ?? 'webhook') === 'webhook' &&
        notification.auth?.basic && {
          auth: {
            basic: {
              username: notification.auth.basic.username ?? '',
              password: notification.auth.basic.password ?? '',
            },
          },
        }),
    })) as ConfigItem.TriggerNotification[],
    isDisabled,
    notes,
    uiFolder,
    otherProps,
  };
};

export const fromRawEval = ({
  operator,
  value,
  ...otherProps
}: RawTriggerEval): ConfigItem.TriggerEval => {
  const result =
    operator === RegexOperator && ConfigItem.isValidRegexVal(value)
      ? {
          operator: RegexOperator,
          value,
          otherProps,
        }
      : {
          operator,
          value,
          otherProps,
        };

  if (result.operator === undefined) {
    delete result.operator;
  }
  if (result.value === undefined) {
    delete result.value;
  }

  return result;
};

const toRawEval = (value: ConfigItem.TriggerEval): RawTriggerEval => {
  const { otherProps, ...base } = value;

  return { ...base, ...otherProps };
};

const fromRawTriggerEvent = (
  rawEvent: RawTriggerEvent,
  components: Record<string, ConfigItem.ReadonlyComponent>
): ConfigItem.ReadonlyTriggerEvent => {
  const { type, ...otherProps } = rawEvent;

  switch (type) {
    case TriggerTypes.PART_ONLINE: {
      return { type: TriggerTypes.PART_ONLINE };
    }
    case TriggerTypes.PART_OFFLINE: {
      return { type: TriggerTypes.PART_OFFLINE };
    }
    case TriggerTypes.PART_DATA_INGESTED: {
      return {
        type: TriggerTypes.PART_DATA_INGESTED,
        dataIngested: {
          dataTypes: (rawEvent.data_ingested?.data_types ?? []) as string[],
        },
        ...otherProps,
      };
    }
    case TriggerTypes.CONDITIONAL_DATA_INGESTED: {
      const { conditional } = rawEvent;

      const captureMethod = conditional?.data_capture_method ?? '';
      const [componentType = '', componentName = '', method = ''] =
        captureMethod.split(':');
      const foundComponent = Object.values(components).find(
        (component) =>
          component.api.subtype === componentType &&
          component.name === componentName
      );
      const rawEvals: RawTriggerEval[] = conditional?.condition?.evals ?? [];
      const evals = rawEvals.map(fromRawEval);

      return {
        type: TriggerTypes.CONDITIONAL_DATA_INGESTED,
        dataCapture: {
          componentId: foundComponent?.id ?? '',
          method,
        },
        unparsedDataCapture: captureMethod,
        evals,
        ...otherProps,
      };
    }
    case TriggerTypes.CONDITIONAL_LOGS_INGESTED: {
      return {
        type: TriggerTypes.CONDITIONAL_LOGS_INGESTED,
        logLevels: (rawEvent.log_levels ?? []) as string[],
        logMessageRegex: (rawEvent.log_message_regex ?? '') as string,
        ...otherProps,
      };
    }
  }

  return { type: '', configType: type };
};

export const toRawTriggerEvent = (
  event: ConfigItem.ReadonlyTriggerEvent,
  components: Readonly<
    Record<ConfigItem.ResourceID, ConfigItem.ReadonlyComponent>
  >
): RawTriggerEvent => {
  if (event.type === TriggerTypes.PART_DATA_INGESTED) {
    return {
      type: TriggerTypes.PART_DATA_INGESTED,
      data_ingested: {
        data_types: [...event.dataIngested.dataTypes],
      },
    };
  }

  if (event.type === TriggerTypes.CONDITIONAL_DATA_INGESTED) {
    const component = components[event.dataCapture.componentId];
    const componentType = component?.api.subtype ?? '';
    const componentName = component?.name ?? '';
    const { method } = event.dataCapture;
    const componentDataCaptureMethod = `${componentType}:${componentName}:${method}`;

    const dataCaptureMethod = component
      ? componentDataCaptureMethod
      : event.unparsedDataCapture;

    if (event.evals.length > 0) {
      return {
        type: TriggerTypes.CONDITIONAL_DATA_INGESTED,
        conditional: {
          data_capture_method: dataCaptureMethod,
          condition: {
            evals: event.evals.map(toRawEval),
          },
        },
      };
    }

    return {
      type: TriggerTypes.CONDITIONAL_DATA_INGESTED,
      conditional: {
        data_capture_method: dataCaptureMethod,
      },
    };
  }

  if (event.type === TriggerTypes.CONDITIONAL_LOGS_INGESTED) {
    return {
      type: TriggerTypes.CONDITIONAL_LOGS_INGESTED,
      log_levels: [...event.logLevels],
      ...(event.logMessageRegex
        ? { log_message_regex: event.logMessageRegex }
        : {}),
    };
  }

  if (event.type === TriggerTypes.UNKNOWN) {
    return {
      type: event.configType,
    };
  }

  return event;
};

export const toRawTrigger = (
  trigger: ConfigItem.ReadonlyTrigger,
  components: Readonly<
    Record<ConfigItem.ResourceID, ConfigItem.ReadonlyComponent>
  >
): RawTriggerConfig => {
  const isConditionalLogsIngested =
    trigger.event.type === TriggerTypes.CONDITIONAL_LOGS_INGESTED;
  const isLiveness = isLivenessTriggerType(trigger.event.type);

  return stripUndefined({
    name: trigger.name,
    event: toRawTriggerEvent(trigger.event, components),
    notifications: trigger.notifications.map((notification) => {
      const baseNotification = {
        type: notification.type,
        value: notification.value,
        ...(notification.type === 'push' &&
          'application' in notification && {
            application: notification.application,
          }),
        ...(notification.type === 'webhook' &&
          notification.auth?.basic && {
            auth: {
              basic: {
                username: notification.auth.basic.username,
                password: notification.auth.basic.password,
              },
            },
          }),
      };

      // Don't include seconds_between_notifications for liveness or conditional logs ingested triggers
      if (isConditionalLogsIngested || isLiveness) {
        return baseNotification;
      }

      return {
        ...baseNotification,
        seconds_between_notifications: notification.secondsBetweenNotifications,
      };
    }),
    disabled: trigger.isDisabled,
    notes: trigger.notes,
    ui_folder: trigger.uiFolder,
  });
};

export const fromRawJob = (
  rawJob: RawJobConfig,
  id: ConfigItem.ResourceID | CreateResourceID
): ConfigItem.ReadonlyJob => {
  const {
    name,
    schedule,
    resource,
    method,
    command,
    log_configuration: logConfiguration,
    notes,
    disabled,
    ui_folder: uiFolder,
    ...otherProps
  } = rawJob;
  const resourceType = ConfigItem.ResourceTypes.JOB;
  const resourceID = getOrCreateID(id, resourceType, name);

  return {
    id: resourceID,
    type: resourceType,
    name,
    schedule,
    resource,
    method,
    command,
    logConfiguration,
    isDisabled: disabled,
    otherProps,
    notes,
    uiFolder,
  };
};

export const toRawJob = (job: ConfigItem.ReadonlyJob): RawJobConfig => {
  return stripUndefined({
    ...job.otherProps,
    name: job.name,
    schedule: job.schedule,
    resource: job.resource,
    method: job.method,
    command: job.command,
    log_configuration: job.logConfiguration,
    disabled: job.isDisabled,
    notes: job.notes,
    ui_folder: job.uiFolder,
  });
};

export const toRawFragmentModOperation = (
  operation: ConfigItem.ReadonlyOverwriteOperation
): Schemas.RawFragmentModOperation => {
  const args: Record<string, unknown> = {};
  for (const mod of operation.arguments) {
    args[mod.fieldPath] = mod.value;
  }
  return { [operation.operator]: args };
};

export const toRawFragmentMod = (
  fragmentMod: ConfigItem.ReadonlyFragmentMod
): Schemas.RawFragmentModConfig =>
  stripUndefined({
    fragment_id: fragmentMod.fragmentID,
    mods: fragmentMod.mods.map((operation) =>
      toRawFragmentModOperation(operation)
    ),
    prefix: fragmentMod.prefix,
  });

export const toRawFragmentMods = (
  fragmentMods: Record<
    FragmentID,
    Record<FragmentPrefix, ConfigItem.ReadonlyFragmentMod>
  >
): Schemas.RawFragmentModConfig[] =>
  Object.values(fragmentMods).flatMap((mod) =>
    Object.values(mod).map(toRawFragmentMod)
  );

const fromRawFragmentModOperation = (
  rawOperation: Schemas.RawFragmentModOperation
): ConfigItem.ReadonlyOverwriteOperation | undefined => {
  const [operator] = Object.keys(
    rawOperation
  ) as ConfigItem.OverwriteOperator[];
  if (Object.keys(rawOperation).length !== 1 || operator === undefined) {
    return undefined;
  }

  const rawArguments = rawOperation[operator];
  if (rawArguments === undefined || Object.keys(rawArguments).length <= 0) {
    return undefined;
  }
  const args = Object.entries(rawArguments).map(([fieldPath, value]) => ({
    fieldPath,
    value,
  }));

  return {
    operator,
    arguments: args,
  };
};

const fromRawFragmentMod = (
  rawFragmentMod: Schemas.RawFragmentModConfig
): ConfigItem.ReadonlyFragmentMod | undefined => {
  const { fragment_id: fragmentID, prefix, mods: rawMods } = rawFragmentMod;
  const mods = rawMods
    .map((mod) => fromRawFragmentModOperation(mod))
    .filter(
      (mod): mod is ConfigItem.ReadonlyOverwriteOperation => mod !== undefined
    );
  return mods.length > 0
    ? {
        fragmentID,
        prefix,
        mods,
      }
    : undefined;
};

export const fromRawFragmentMods = (
  rawFragmentMods: Schemas.RawFragmentModConfig[]
): Record<
  FragmentID,
  Record<ConfigItem.FragmentPrefix, ConfigItem.ReadonlyFragmentMod>
> => {
  const fragmentMods: Record<
    FragmentID,
    Record<ConfigItem.FragmentPrefix, ConfigItem.ReadonlyFragmentMod>
  > = {};
  for (const rawFragmentMod of rawFragmentMods) {
    const fragmentMod = fromRawFragmentMod(rawFragmentMod);
    if (fragmentMod !== undefined) {
      const prefix = fragmentMod.prefix ?? '';

      fragmentMods[rawFragmentMod.fragment_id] ??= {};

      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      fragmentMods[rawFragmentMod.fragment_id]![prefix] = fragmentMod;
    }
  }
  return fragmentMods;
};

export const toRawResource = (
  resource: ConfigItem.ReadonlyResource,
  part?: ReadonlyMachineConfigPart
): JsonObject | undefined => {
  switch (resource.type) {
    case ConfigItem.ResourceTypes.PROCESS: {
      return toRawProcess(resource) as JsonObject;
    }
    case ConfigItem.ResourceTypes.PACKAGE: {
      return toRawPackage(resource) as JsonObject;
    }
    case ConfigItem.ResourceTypes.LOCAL_MODULE: {
      return toRawLocalModule(resource) as JsonObject;
    }
    case ConfigItem.ResourceTypes.REGISTRY_MODULE: {
      return toRawRegistryModule(resource) as JsonObject;
    }
    case ConfigItem.ResourceTypes.REMOTE: {
      return toRawRemote(resource) as JsonObject;
    }
    case ConfigItem.ResourceTypes.COMPONENT: {
      return toRawComponent(resource) as JsonObject;
    }
    case ConfigItem.ResourceTypes.SERVICE: {
      return toRawService(resource) as JsonObject;
    }
    case ConfigItem.ResourceTypes.TRIGGER: {
      return toRawTrigger(resource, part?.components ?? {}) as JsonObject;
    }
    case ConfigItem.ResourceTypes.JOB: {
      return toRawJob(resource) as JsonObject;
    }
    case ConfigItem.ResourceTypes.FRAGMENT_IMPORT: {
      return toRawFragmentImport(resource) as JsonObject;
    }
    default: {
      return undefined;
    }
  }
};

export const fromRawResource = (
  resource: Schemas.RawResourceConfig,
  resourceID: ConfigItem.ResourceID | CreateResourceID,
  type: ConfigItem.ResourceType | undefined,
  components?: Record<string, ConfigItem.ReadonlyComponent>
): ConfigItem.ReadonlyResource | undefined => {
  switch (type) {
    case ConfigItem.ResourceTypes.PROCESS: {
      return fromRawProcess(resource as RawProcessConfig, resourceID);
    }
    case ConfigItem.ResourceTypes.PACKAGE: {
      return fromRawPackage(resource as Schemas.RawPackageConfig, resourceID);
    }
    case ConfigItem.ResourceTypes.LOCAL_MODULE: {
      return fromRawLocalModule(resource as RawLocalModuleConfig, resourceID);
    }
    case ConfigItem.ResourceTypes.REGISTRY_MODULE: {
      return fromRawRegistryModule(
        resource as RawRegistryModuleConfig,
        resourceID
      );
    }
    case ConfigItem.ResourceTypes.REMOTE: {
      return fromRawRemote(resource as RawRemoteConfig, resourceID);
    }
    case ConfigItem.ResourceTypes.COMPONENT: {
      return fromRawComponent(
        resource as Schemas.RawComponentConfig,
        resourceID
      );
    }
    case ConfigItem.ResourceTypes.SERVICE: {
      return fromRawService(resource as Schemas.RawServiceConfig, resourceID);
    }
    case ConfigItem.ResourceTypes.TRIGGER: {
      return fromRawTrigger(
        resource as RawTriggerConfig,
        resourceID,
        components ?? {}
      );
    }
    case ConfigItem.ResourceTypes.JOB: {
      return fromRawJob(resource as RawJobConfig, resourceID);
    }
    case ConfigItem.ResourceTypes.FRAGMENT_IMPORT: {
      return fromRawFragmentImport(
        resource as Schemas.RawFragmentImportConfig,
        resourceID
      );
    }
    default: {
      return fromRawUnknownResource(resource);
    }
  }
};

export const toRawSingletonResource = (
  resource: ConfigItem.ReadonlySingletonResource
): JsonObject | undefined => {
  switch (resource.type) {
    case SingletonResourceTypes.AGENT: {
      return toRawAgent(resource) as JsonObject;
    }
    case SingletonResourceTypes.BILLING: {
      return toRawBilling(resource) as JsonObject;
    }
    case SingletonResourceTypes.NETWORK: {
      return toRawNetwork(resource) as JsonObject;
    }
    case SingletonResourceTypes.MAINTENANCE: {
      return toRawMaintenance(resource) as JsonObject;
    }
    default: {
      return undefined;
    }
  }
};

const fromRawFragmentImport = (
  rawFragmentImport: Schemas.RawFragmentImportConfig,
  id: ConfigItem.ResourceID | CreateResourceID
): ConfigItem.ReadonlyFragmentImport => {
  if (typeof rawFragmentImport === 'string') {
    const fragmentID = rawFragmentImport;
    return {
      id: getOrCreateID(
        id,
        ConfigItem.ResourceTypes.FRAGMENT_IMPORT,
        fragmentID
      ),
      type: ConfigItem.ResourceTypes.FRAGMENT_IMPORT,
      fragmentID,
      importType: ConfigItem.FragmentImportTypes.STRING,
      otherProps: {},
    };
  }

  const {
    id: fragmentID,
    version,
    variables,
    ui_folder: uiFolder,
    prefix,
    disabled,
  } = rawFragmentImport;
  return stripUndefined({
    id: getOrCreateID(id, ConfigItem.ResourceTypes.FRAGMENT_IMPORT, fragmentID),
    type: ConfigItem.ResourceTypes.FRAGMENT_IMPORT,
    importType: ConfigItem.FragmentImportTypes.OBJECT,
    fragmentID,
    version,
    variables,
    uiFolder,
    otherProps: {},
    prefix,
    isDisabled: disabled,
  });
};

export const toRawFragmentImport = (
  fragmentImport: ConfigItem.ReadonlyFragmentImport
): Schemas.RawFragmentImportConfig => {
  // The string form (`"abc123"`) cannot carry `disabled` — promote to object form when set.
  if (
    fragmentImport.importType === ConfigItem.FragmentImportTypes.STRING &&
    !fragmentImport.isDisabled
  ) {
    return fragmentImport.fragmentID;
  }
  const { fragmentID, version, variables, uiFolder, prefix, isDisabled } =
    fragmentImport;
  return stripUndefined({
    id: fragmentID,
    version,
    variables,
    ui_folder: uiFolder,
    prefix,
    disabled: isDisabled,
  });
};

/** Take a raw resource parent (part or fragment) and normalize its resources. */
export const fromRawResourceParent = (
  rawParent: Schemas.RawPartConfig | Schemas.RawFragmentConfig,
  parentID: string
): ConfigItem.ResourceParent => {
  const {
    agent,
    billing,
    network,
    maintenance,
    components = [],
    services = [],
    modules = [],
    remotes = [],
    processes = [],
    packages = [],
    triggers = [],
    jobs = [],
    fragments = [],
  } = rawParent;

  const registryModules = modules.flatMap((mod) =>
    mod.type === 'registry' ? mod : []
  );
  const localModules = modules.flatMap((mod) =>
    mod.type === 'registry' ? [] : mod
  );

  const normalizedComponents: Record<string, ConfigItem.ReadonlyComponent> =
    normalize(parentID, components, fromRawComponent);

  return {
    agent: fromRawAgent(agent),
    billing: fromRawBilling(billing),
    network: fromRawNetwork(network),
    maintenance: fromRawMaintenance(maintenance),
    components: normalizedComponents,
    services: normalize(parentID, services, fromRawService),
    registryModules: normalize(
      parentID,
      registryModules as RawRegistryModuleConfig[],
      fromRawRegistryModule
    ),
    localModules: normalize(
      parentID,
      localModules as RawLocalModuleConfig[],
      fromRawLocalModule
    ),
    remotes: normalize(parentID, remotes, fromRawRemote),
    processes: normalize(parentID, processes, fromRawProcess),
    packages: normalize(parentID, packages, fromRawPackage),
    triggers: normalize(parentID, triggers, (rawTrigger, id) =>
      fromRawTrigger(rawTrigger, id, normalizedComponents)
    ),
    jobs: normalize(parentID, jobs, fromRawJob),
    fragmentImports: normalize(parentID, fragments, fromRawFragmentImport),
  };
};

/** Normalize a set of resource configs to a record keyed by resource ID. */
const normalize = <T, R extends { id: ConfigItem.ResourceID }>(
  // TODO(mc, 2024-02-14): add ResourceID when opaque and union would mean something
  parentID: ConfigItem.PartID,
  rawResources: T[],
  mapper: (resource: T, createID: CreateResourceID) => R
): Record<ConfigItem.ResourceID, R> => {
  return Object.fromEntries(
    rawResources.map((rawResource, index) => {
      const createStableID = makeCreateStableID(parentID, index);
      const resource = mapper(rawResource, createStableID);
      return [resource.id, resource];
    })
  );
};

/**
 * Bind a parent ID to a `CreateResourceID` function.
 *
 * @returns an ID creator function.
 */
const makeCreateStableID =
  (parentID: ConfigItem.PartID, resourceIndex: number): CreateResourceID =>
  (resourceType, resourceName) => {
    return ConfigItem.stableResourceID(
      parentID,
      resourceType,
      resourceName,
      resourceIndex
    );
  };

const toRawAPI = (
  api: APITriplet
): { api: ResourceTripletString } | { namespace?: string; type: string } => {
  // handle if the api is secretly a fragment variable property.
  // this is a hack abusing the underlying types. if a variable is used
  // to define the api or model types in a fragment config, we secretly
  // pass that variable object along and pretend it's an api or model
  // object, then handle that case in specific places relevant to the fragment
  // page only
  if (isVariableObject(api)) {
    return { api } as unknown as { api: ResourceTripletString }; // type trickery
  }

  return api.isBackCompatFormat
    ? { namespace: api.namespace, type: api.subtype }
    : { api: joinResourceTriplet(api) };
};

const toRawModel = (model: ModelTriplet): string | undefined => {
  // handle if the model is secretly a fragment variable property.
  // this is a hack abusing the underlying types. if a variable is used
  // to define the api or model types in a fragment config, we secretly
  // pass that variable object along and pretend it's an api or model
  // object, then handle that case in specific places relevant to the fragment
  // page only
  if (hasVariables(model)) {
    return model as unknown as string; // type trickery
  }

  if (model.isBackCompatFormat) {
    return isBuiltinModel(model, RDK_MODEL_NAME_BUILTIN)
      ? undefined
      : model.name;
  }

  return joinResourceTriplet(model);
};
